import React, { useEffect, useState } from "react"
import ClientReportCard from "../../components/ClientReportCard.molecule"
import useReportFiltersContext from "../../../../hooks/contextConsumers/useReportFiltersContext.hook"
import NavigateToBudgetButton from "../../components/NavigateToBudgetButton.molecule"
import {
  getEstimatedBudgetEnd,
  isBudgetTypeCredit,
} from "../../../../utils/budget.utils"
import { DateTime } from "luxon"
import { getClientPeriodBudgetStateByIdAndPeriodType } from "../../../../services/clientReport.service"
import {
  budgetTypeCreditTemplate,
  budgetTypeDebitTemplate,
} from "../../components/ClientBudgetCardTemplate.molecule"
import { getEstimatedEndDateToCompletePoints } from "../../../../utils/clientReport.utils"

const ClientPeriodBudgetState = ({ refresh, selectedPeriod = {} }) => {
  const [data, setData] = useState({})
  const [loading, setLoading] = useState(true)
  const { clientReportSelectedClient, isClientReportSelectedClientBillable } =
    useReportFiltersContext()

  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal
    fetchPeriodBudgetState(signal)
    return () => {
      controller.abort()
    }
  }, [selectedPeriod?.id, refresh])

  const fetchPeriodBudgetState = async (signal) => {
    setLoading(true)
    try {
      if (!selectedPeriod?.id) {
        setData({})
        return setLoading(false)
      }
      const response = await getClientPeriodBudgetStateByIdAndPeriodType(
        selectedPeriod.id,
        selectedPeriod.clientConsumptionPeriodType,
        { signal }
      )
      if (!response.success) {
        throw new Error(response.message)
      }
      const result = response.result
      if (!result) {
        setData({})
        return setLoading(false)
      }
      const estimatedPointsFinishDate = getEstimatedEndDateToCompletePoints(
        result?.sprintProgressResult
      )
      if (estimatedPointsFinishDate) {
        const estimatedBudgetEnd = getEstimatedBudgetEnd(
          result?.totalBudget - result?.totalBilledTime,
          result?.lastEightWeeksTruncatedMean * 4,
          result?.lastEightWeeksTruncatedMean * 4
        )?.estimatedEndMaxDate
        let remainingWeeks = estimatedBudgetEnd.diff(
          DateTime.fromFormat(estimatedPointsFinishDate, "dd/MM/yyyy"),
          "weeks"
        )?.weeks
        if (
          remainingWeeks < 0 &&
          DateTime.fromFormat(estimatedPointsFinishDate, "dd/MM/yyyy") >
            estimatedBudgetEnd
        ) {
          remainingWeeks = 0.1
        }
        result.remainingWeeks = remainingWeeks
      }
      setData(result)
    } catch (error) {
      console.error("Error fetching sprint progress data:", error)
    } finally {
      setLoading(false)
    }
  }
  return (
    <ClientReportCard
      title={"Estado del presupuesto"}
      headerButton={
        <NavigateToBudgetButton
          clientId={clientReportSelectedClient}
          isClientBillable={isClientReportSelectedClientBillable}
          loading={loading}
        />
      }
      iconClassName="pi pi-wallet text-3xl"
      className="flex-1 font-cairo flex-shrink-0"
    >
      {isBudgetTypeCredit(data?.clientBudgetTypeId) ?
        budgetTypeCreditTemplate({
          suggestedMonthlyBudgetLimit: data?.suggestedMonthlyBudgetLimit,
          lastMonthBilledTime: data?.lastMonthBilledTime,
          estimatedEndDate: DateTime.now()
            .plus({ months: 1 })
            .startOf("month")
            .toFormat("dd/MM/yyyy"),
          loading,
        })
      : budgetTypeDebitTemplate({
          totalBudget: data?.totalBudget,
          historicalBudget: data?.historicalBudget,
          totalBilledTime: data?.totalBilledTime,
          hasBudget: data?.hasBudget,
          lastEightWeeksTruncatedMean: data?.lastEightWeeksTruncatedMean,
          remainingWeeks: data?.remainingWeeks,
          loading,
        })
      }
    </ClientReportCard>
  )
}

export default ClientPeriodBudgetState
